<template>
  <v-modal
    id="the-case-day-item-pax-selection-modal"
    class-name="modal-fullscreen-sm-down"
    size="lg"
    @closed="closeModal"
  >
    <modal-header>
      <modal-title>
        <div class="d-flex align-items-center gap-8">
          <v-icon icon="user-settings" size="sm" />
          <span>Pax Selection</span>
        </div>
        <div v-if="currentResourceName" class="pax-selection-subtitle text-muted text-xs fw-normal mt-4">
          {{ currentResourceName }}
          <span class="ms-8" :class="selectedCountClass">
            ({{ localSelectedPaxIds.length }} {{ $t('general.shared.selected') }})
          </span>
        </div>
      </modal-title>
    </modal-header>

    <modal-body>
      <template v-if="sortedSelectedResource && sortedSelectedResource.length > 0">
        <div class="pax-selection-list">
          <div
            v-for="p in sortedSelectedResource"
            :key="`${p.id}-${checkboxKey}`"
            class="pax-selection-row"
            :class="{
              'pax-row-selected': localSelectedPaxIds.includes(getTravellerId(p)),
              'pax-row-disabled': isPaxUsedInAnotherResource(getTravellerId(p)) && !localSelectedPaxIds.includes(getTravellerId(p)),
            }"
          >
            <v-checkbox
              :model-value="localSelectedPaxIds.includes(getTravellerId(p))"
              @update:model-value="(val) => togglePax(val, getTravellerId(p), p)"
              :id="`pax-selection-${getTravellerId(p)}`"
              :for-key="`pax-selection-${getTravellerId(p)}`"
              :label="' '"
              class="form-check-inline pax-selection-checkbox"
              label-class="ms-0"
              :isDisabled="
                isPaxUsedInAnotherResource(getTravellerId(p)) && !localSelectedPaxIds.includes(getTravellerId(p))
              "
            />

            <div class="pax-selection-info">
              <div class="pax-selection-name">
                <v-icon
                  v-if="localSelectedPaxIds.includes(getTravellerId(p))"
                  icon="check-circle-fill"
                  size="xs"
                  class="text-success me-4"
                />
                {{ labelForPax(p).fullName }}
                <span v-if="labelForPax(p).email" class="pax-selection-email ms-8">
                  <span class="pax-selection-divider mx-4">|</span>{{ labelForPax(p).email }}
                </span>
              </div>
              <div v-if="labelForPax(p).alreadySelected" class="pax-selection-warning">
                <v-icon icon="exclamation-triangle-fill" size="xs" class="me-4" />
                {{ $t("general.caseDayItem.paxAlreadyAssigned", { resource: labelForPax(p).assignedTo }) }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="pax-selection-empty">
          <v-icon icon="group" size="lg" class="text-muted mb-12" />
          <p class="text-muted">No paxes available.</p>
        </div>
      </template>
    </modal-body>

    <modal-footer>
      <div class="d-flex justify-content-between align-items-center w-100">
        <div class="pax-selection-summary">
          <span class="fw-bold">{{ localSelectedPaxIds.length }}</span>
          <span class="text-muted ms-4">{{ $t('general.shared.selected') }}</span>
          <span class="text-muted mx-8">/</span>
          <span class="text-muted">{{ sortedSelectedResource.length }}</span>
          <span class="text-muted ms-4">{{ $t('general.shared.pax') }}</span>
        </div>
        <v-button
          class="btn-sm btn-success cursor-pointer"
          size="sm"
          variant="success"
          @click="$emit('closed')"
        >
          {{ $t("general.button.confirmSelection") }}
        </v-button>
      </div>
    </modal-footer>
  </v-modal>
</template>

<script setup lang="ts" name="TheCaseDayItemPaxSelection">
import { ref, computed, PropType, watch } from "vue";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import ModalFooter from "@/components/vendor/basic/modal/ModalFooter.vue";
import { VCheckbox } from "@uniqoders/form";
import { useToast } from "vue-toastification";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import api from "@/api";

const emit = defineEmits<{
  (e: "closed"): void;
  (e: "refresh"): void;
}>();

const toast = useToast();

const props = defineProps({
  selectedResource: { type: Array as PropType<any[]>, required: true },
  agency: { type: String, required: true },
  caseId: { type: Number, required: true },
  caseDayId: { type: Number, required: true },
  itemId: { type: Number, required: true },
  caseDayItemResources: { type: Array as PropType<any[]>, required: true },
  supplierResourceId: { type: Number, required: false, default: null },
  // For custom (extra service) resources supplier_resource_id is null, so the
  // specific case_day_item_resource row id is used to identify the resource.
  resourceId: { type: Number, required: false, default: null },
});

const localSelectedPaxIds = ref<number[]>([]);

// Incremented on API error to force VCheckbox re-render and revert its
// internal localValue state (which updates on click before the async result).
const checkboxKey = ref(0);

// Custom (extra service) resources have no supplier_resource_id. They are
// independent services — a pax can be assigned to several of them at once.
const isCustomResource = computed(() => !props.supplierResourceId);

// Whether a caseDayItemResources entry is the resource group currently being
// edited. Custom resources match on their own row id; linked resources group
// by supplier_resource_id.
const matchesCurrentResource = (r: any): boolean => {
  if (isCustomResource.value) {
    const rowId = r?.data?.id ?? r?.id ?? null;
    return rowId !== null && Number(rowId) === Number(props.resourceId);
  }
  const rSupplierId = r?.data?.supplier_resource_id ?? r?.supplier_resource_id ?? null;
  return Number(rSupplierId) === Number(props.supplierResourceId);
};

// Resource name for the modal header
const currentResourceName = computed(() => {
  const r = localResources.value.find(matchesCurrentResource);
  return r?.data?.name ?? r?.name ?? '';
});

// Color class for the selected count in the header
const selectedCountClass = computed(() => {
  return localSelectedPaxIds.value.length > 0 ? 'text-success fw-bold' : 'text-muted';
});

// Local copy of caseDayItemResources so we can update pax assignments
// immediately after add/remove, without waiting for parent refresh.
const localResources = ref<any[]>([]);

const syncLocalResources = () => {
  localResources.value = JSON.parse(JSON.stringify(props.caseDayItemResources ?? []));
};

const sortedSelectedResource = computed(() => {
  if (!Array.isArray(props.selectedResource)) return [];
  return [...props.selectedResource].sort((a, b) => {
    const nameA = (a?.pax?.name || a?.traveller?.name || a?.name || '').toLowerCase();
    const nameB = (b?.pax?.name || b?.traveller?.name || b?.name || '').toLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    const idA = a?.traveller_id || a?.pax?.id || a?.id || 0;
    const idB = b?.traveller_id || b?.pax?.id || b?.id || 0;
    return idA - idB;
  });
});

const getTravellerId = (p: any): number | null => p?.traveller_id ?? p?.travellerId ?? p?.pax?.id ?? p?.id ?? null;

// Collect all pax from a resource group, iterating through ALL rooms
// (tourDayItemResources), not just the first room's selectedPax.
const collectAllPaxFromGroup = (resource: any): any[] => {
  const allPax: any[] = [];

  // First room's pax (from data.selectedPax)
  const firstRoomPax = resource?.data?.selectedPax ?? resource?.selectedPax ?? [];
  if (Array.isArray(firstRoomPax)) {
    allPax.push(...firstRoomPax);
  }

  // All rooms in the group (tourDayItemResources)
  const rooms = resource?.tourDayItemResources ?? resource?.data?.tourDayItemResources ?? [];
  if (Array.isArray(rooms)) {
    for (const room of rooms) {
      const roomPax = room?.paxes ?? room?.selectedPax ?? [];
      if (Array.isArray(roomPax)) {
        allPax.push(...roomPax);
      }
    }
  }

  return allPax;
};

// Find which room (case_day_item_resource id) within a group has a specific pax
const findRoomIdForPax = (resource: any, paxId: number): number | null => {
  const rooms = resource?.tourDayItemResources ?? resource?.data?.tourDayItemResources ?? [];
  if (Array.isArray(rooms)) {
    for (const room of rooms) {
      const roomPax = room?.paxes ?? room?.selectedPax ?? [];
      if (Array.isArray(roomPax)) {
        for (const px of roomPax) {
          const tid = px?.traveller_id ?? px?.travellerId ?? px?.pax?.id ?? px?.id ?? null;
          if (tid !== null && Number(tid) === Number(paxId)) {
            return room?.id ?? room?.data?.id ?? null;
          }
        }
      }
    }
  }
  return null;
};

// Find an available room for adding a new pax: prefer a room that doesn't
// already have this pax. Returns the first such room's id.
const findAvailableRoomForPax = (resource: any, paxId: number): number | null => {
  const rooms = resource?.tourDayItemResources ?? resource?.data?.tourDayItemResources ?? [];
  if (Array.isArray(rooms) && rooms.length > 0) {
    // First, try to find a room that already has this pax (idempotent)
    const existingRoom = findRoomIdForPax(resource, paxId);
    if (existingRoom) return existingRoom;

    // Otherwise, find the first room (prefer empty ones)
    let firstEmpty: number | null = null;
    for (const room of rooms) {
      const roomPax = room?.paxes ?? room?.selectedPax ?? [];
      const roomId = room?.id ?? room?.data?.id ?? null;
      if (roomId && (!Array.isArray(roomPax) || roomPax.length === 0)) {
        if (firstEmpty === null) firstEmpty = roomId;
      }
    }
    if (firstEmpty !== null) return firstEmpty;

    // Fall back to first room
    return rooms[0]?.id ?? rooms[0]?.data?.id ?? null;
  }
  // No tourDayItemResources, fall back to data.id
  return resource?.data?.id ?? resource?.id ?? null;
};

const findCurrentResourceObject = () => {
  if (!Array.isArray(localResources.value)) return null;
  return localResources.value.find(matchesCurrentResource) ?? null;
};

// Find ALL resources with the same supplier_resource_id (grouped resources).
// Custom resources are never grouped — each is its own product.
const findAllGroupedResourceObjects = () => {
  if (!Array.isArray(localResources.value)) return [];
  return localResources.value.filter(matchesCurrentResource);
};

const initializeLocalSelection = () => {
  localSelectedPaxIds.value = [];

  // Collect pax from ALL grouped resources with the same supplier_resource_id,
  // iterating through ALL rooms in each group (tourDayItemResources).
  const groupedResources = findAllGroupedResourceObjects();
  if (groupedResources.length === 0) return;

  for (const currentResource of groupedResources) {
    const allPax = collectAllPaxFromGroup(currentResource);

    for (const s of allPax) {
      const tid = s?.traveller_id ?? s?.travellerId ?? s?.pax?.id ?? s?.id ?? null;
      if (tid !== null && tid !== undefined) {
        const numTid = Number(tid);
        if (!localSelectedPaxIds.value.includes(numTid)) {
          localSelectedPaxIds.value.push(numTid);
        }
      }
    }
  }
};

const isPaxUsedInAnotherResource = (paxId: number | null): boolean => {
  if (!paxId) return false;
  // Extra services never conflict — the same pax may hold several of them.
  if (isCustomResource.value) return false;
  if (!Array.isArray(localResources.value)) return false;

  for (const resource of localResources.value) {
    const resSupplierId = resource?.data?.supplier_resource_id ?? resource?.supplier_resource_id ?? null;
    // skip ALL resources in the same group (same supplier_resource_id) AND
    // custom resources (extra services never block supplier products either)
    if (resSupplierId == null || Number(resSupplierId) === Number(props.supplierResourceId)) continue;

    const allPax = collectAllPaxFromGroup(resource);
    for (const px of allPax) {
      const otherTid = px?.traveller_id ?? px?.travellerId ?? px?.pax?.id ?? px?.id ?? null;
      if (otherTid !== null && Number(otherTid) === Number(paxId)) {
        return true;
      }
    }
  }
  return false;
};

// Find which resource (room) a pax is assigned to, for display purposes
const paxAssignedResourceName = (paxId: number | null): string | null => {
  if (!paxId) return null;
  if (isCustomResource.value) return null;
  if (!Array.isArray(localResources.value)) return null;

  for (const resource of localResources.value) {
    const resSupplierId = resource?.data?.supplier_resource_id ?? resource?.supplier_resource_id ?? null;
    // skip ALL resources in the same group AND custom resources
    if (resSupplierId == null || Number(resSupplierId) === Number(props.supplierResourceId)) continue;

    const allPax = collectAllPaxFromGroup(resource);
    for (const px of allPax) {
      const otherTid = px?.traveller_id ?? px?.travellerId ?? px?.pax?.id ?? px?.id ?? null;
      if (otherTid !== null && Number(otherTid) === Number(paxId)) {
        return resource?.data?.name ?? resource?.name ?? null;
      }
    }
  }
  return null;
};

// ---------- label builder ----------
const labelForPax = (p: any) => {
  // Attempt to find the "inner" traveller object if p is a wrapper
  const inner = p?.pax ?? p?.traveller ?? p;

  // Sometimes there's double nesting
  const data = inner?.pax ?? inner?.traveller ?? inner;

  // Try all possible name/surname fields
  const name = data?.name ?? inner?.name ?? p?.name ?? "";
  const surname = data?.surname ?? data?.surname1 ?? data?.surname2 ??
                  inner?.surname ?? inner?.surname1 ?? inner?.surname2 ??
                  p?.surname ?? p?.surname1 ?? p?.surname2 ?? "";

  const alias = data?.alias ?? inner?.alias ?? p?.alias ?? "";

  let fullName = `${name} ${surname}`.trim();
  if (!fullName && alias) {
    fullName = alias;
  }
  if (!fullName) {
    fullName = "N/A";
  }

  const email = data?.email ?? inner?.email ?? p?.email ?? "";

  const travellerId = getTravellerId(p);
  const usedInAnotherResource = travellerId ? isPaxUsedInAnotherResource(travellerId) : false;
  const inCurrentSelection = travellerId ? localSelectedPaxIds.value.includes(Number(travellerId)) : false;
  // Only show the warning when the pax is in another resource group AND not in
  // the current group. If the pax is in the current group (even if also in
  // another group), no warning is shown — the user can simply uncheck to remove.
  const alreadySelected = !!usedInAnotherResource && !inCurrentSelection;
  const assignedTo = alreadySelected ? paxAssignedResourceName(travellerId) : null;

  return {
    fullName,
    name,
    email,
    alreadySelected,
    assignedTo,
    inCurrentSelection,
  };
};

// Resolve the correct case_day_item_resource id for a given pax:
// - When removing: find the room that currently has this pax
// - When adding: find an available room (empty room or first room)
const getCurrentCaseDayItemResourceId = (paxId?: number | null, isRemoving = false): number | null => {
  const r = findCurrentResourceObject();
  if (!r) return props.supplierResourceId ?? null;

  if (isRemoving && paxId) {
    const roomId = findRoomIdForPax(r, paxId);
    if (roomId) return roomId;
  }

  if (paxId) {
    const roomId = findAvailableRoomForPax(r, paxId);
    if (roomId) return roomId;
  }

  return r?.data?.id;
};

// Immediately update localResources after adding a pax, so the UI reflects
// the assignment without waiting for the parent to refresh props.
const addPaxToLocalResources = (resourceId: number, travellerId: number, paxData: any) => {
  for (const resource of localResources.value) {
    // Check if this resource group contains the room
    const rooms = resource?.tourDayItemResources ?? [];
    if (Array.isArray(rooms)) {
      for (const room of rooms) {
        if (Number(room?.id) === Number(resourceId)) {
          const roomPax = room?.paxes ?? (room.paxes = []);
          if (Array.isArray(roomPax)) {
            roomPax.push({ traveller_id: travellerId, pax: paxData?.pax ?? paxData?.traveller ?? paxData });
          }
          return;
        }
      }
    }
    // Also check data.id (first room / single room case)
    const resId = resource?.data?.id ?? resource?.id ?? null;
    if (Number(resId) === Number(resourceId)) {
      const selectedPax = resource?.data?.selectedPax ?? resource?.selectedPax;
      if (Array.isArray(selectedPax)) {
        selectedPax.push({ traveller_id: travellerId, pax: paxData?.pax ?? paxData?.traveller ?? paxData });
      }
      return;
    }
  }
};

// Immediately update localResources after removing a pax, so the UI no longer
// shows "already assigned" for a pax that was just removed.
const removePaxFromLocalResources = (travellerId: number) => {
  for (const resource of localResources.value) {
    // Custom resources are independent — only touch the one being edited
    if (isCustomResource.value && !matchesCurrentResource(resource)) continue;
    // Check all rooms in the group
    const rooms = resource?.tourDayItemResources ?? [];
    if (Array.isArray(rooms)) {
      for (const room of rooms) {
        const roomPax = room?.paxes;
        if (!Array.isArray(roomPax)) continue;
        const idx = roomPax.findIndex((sp: any) => {
          const tid = sp?.traveller_id ?? sp?.travellerId ?? sp?.pax?.id ?? sp?.id ?? null;
          return Number(tid) === Number(travellerId);
        });
        if (idx > -1) {
          roomPax.splice(idx, 1);
        }
      }
    }
    // Also check data.selectedPax (first room)
    const selectedPax = resource?.data?.selectedPax ?? resource?.selectedPax;
    if (Array.isArray(selectedPax)) {
      const idx = selectedPax.findIndex((sp: any) => {
        const tid = sp?.traveller_id ?? sp?.travellerId ?? sp?.pax?.id ?? sp?.id ?? null;
        return Number(tid) === Number(travellerId);
      });
      if (idx > -1) {
        selectedPax.splice(idx, 1);
      }
    }
  }
};

const togglePax = async (checked: boolean, paxId: number | null, p: any) => {
  if (!paxId) return;

  if (isPaxUsedInAnotherResource(paxId) && !localSelectedPaxIds.value.includes(Number(paxId))) return;

  const traveller_id = getTravellerId(p) ?? paxId;
  const caseDayItemResourceId = getCurrentCaseDayItemResourceId(Number(paxId), !checked);

  if (!caseDayItemResourceId) {
    console.warn("Unable to resolve caseDayItemResourceId for API call — toggle aborted.");
    return;
  }

  if (checked) {
    if (!localSelectedPaxIds.value.includes(Number(paxId))) {
      try {
        await api.case.addCaseDayItemResourcePax(
          props.agency,
          props.caseId,
          props.caseDayId,
          props.itemId,
          caseDayItemResourceId,
          { traveller_id }
        );
        localSelectedPaxIds.value.push(Number(paxId));
        // Update localResources so isPaxUsedInAnotherResource reflects the change immediately
        addPaxToLocalResources(caseDayItemResourceId, Number(paxId), p);
        emit("refresh");
      } catch (e: any) {
        console.error("addCaseDayItemResourcePax error", e);
        toast.error(e?.response?.data?.message ?? "Error adding pax");
        // Force VCheckbox to revert: the component has internal localValue state
        // that updates on click. Since modelValue didn't change (stayed false),
        // the watch won't fire. Incrementing checkboxKey forces a re-render,
        // re-initializing localValue from modelValue (false).
        checkboxKey.value++;
      }
    }
  } else {
    try {
      await api.case.removeCaseDayItemResourcePax(
        props.agency,
        props.caseId,
        props.caseDayId,
        props.itemId,
        caseDayItemResourceId,
        paxId
      );
      localSelectedPaxIds.value = localSelectedPaxIds.value.filter((id) => Number(id) !== Number(paxId));
      // Update localResources so isPaxUsedInAnotherResource reflects the removal immediately
      removePaxFromLocalResources(Number(paxId));
      emit("refresh");
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Error removing pax");
      console.error("removeCaseDayItemResourcePax error", e);
      // Force VCheckbox to revert (same reason as add error above)
      checkboxKey.value++;
    }
  }
};

const closeModal = () => {
  emit("closed");
};

watch(
  () => [props.selectedResource, props.caseDayItemResources, props.supplierResourceId],
  () => {
    syncLocalResources();
    initializeLocalSelection();
  },
  { immediate: true, deep: true }
);
</script>

<style scoped>
.pax-selection-subtitle {
  font-size: 0.8rem;
  margin-top: 2px;
}

.pax-selection-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pax-selection-row {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.15s ease;
  background: #fff;
}

.pax-selection-row:hover {
  border-color: #c0c0c0;
  background: #fafafa;
}

.pax-row-selected {
  border-color: #28a745;
  background: #f0fff4;
}

.pax-row-selected:hover {
  border-color: #28a745;
  background: #e8f5e9;
}

.pax-row-disabled {
  opacity: 0.55;
  background: #f5f5f5;
}

.pax-row-disabled:hover {
  background: #f0f0f0;
}

.pax-selection-checkbox {
  margin-top: 2px;
  margin-right: 16px;
  flex-shrink: 0;
}

.pax-selection-info {
  flex: 1;
  min-width: 0;
}

.pax-selection-name {
  color: #1a1a1a;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  line-height: 1.4;
}

.pax-selection-email {
  color: #888;
  font-size: 0.8rem;
  font-style: italic;
}

.pax-selection-divider {
  color: #ccc;
  font-style: normal;
}

.pax-selection-warning {
  color: #dc3545;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 4px;
  display: flex;
  align-items: center;
  line-height: 1.3;
}

.pax-selection-empty {
  text-align: center;
  padding: 48px 16px;
}

.pax-selection-summary {
  font-size: 0.85rem;
}
</style>
